import React, { useEffect, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Spinner } from '@wix/cli-ui-kit';
import { fields, isType, match, variant } from 'variant';
import type { VariantOf } from 'variant';
import type { GenerationTaskResult } from './hooks/use-generation-task.js';
import { InstallDependencies } from './InstallDependencies.js';
import { GenerationSuccessMessage } from './GenerationSuccessMessage.js';
import { useWizardStep } from './Wizard.js';

export const CompleteGenerationMessage = variant({
  SuccessMessage: fields<{
    learnMoreUrl: string;
  }>(),
  CustomSuccessMessage: fields<{
    comp: ReactNode;
  }>(),
});

export type CompleteGenerationMessage = VariantOf<
  typeof CompleteGenerationMessage
>;

interface CompleteGenerationProps {
  generationTask: GenerationTaskResult;
  extensionType: string;
  route?: string;
  successMessage: CompleteGenerationMessage;
}

export const CompleteGeneration: FC<CompleteGenerationProps> = ({
  generationTask,
  extensionType,
  route,
  successMessage,
}) => {
  const { completeWizard } = useWizardStep();

  const [finishedInstallation, setFinishedInstallation] =
    useState<boolean>(false);

  const isInstallationNotNeeded =
    isType(generationTask, 'Success') && !generationTask.hasUpdatedDependencies;

  useEffect(() => {
    if (isInstallationNotNeeded) {
      completeWizard();
      setFinishedInstallation(true);
    }
  }, [isInstallationNotNeeded, completeWizard]);

  return useMemo(
    () =>
      match(generationTask, {
        Success: ({ hasUpdatedDependencies, newFiles }) => (
          <>
            {hasUpdatedDependencies && (
              <InstallDependencies
                onSubmit={() => completeWizard()}
                onFinish={() => setFinishedInstallation(true)}
              />
            )}
            {finishedInstallation &&
              match(successMessage, {
                CustomSuccessMessage: ({ comp }) => comp,
                SuccessMessage: ({ learnMoreUrl }) => (
                  <GenerationSuccessMessage
                    extensionType={extensionType}
                    generatedFiles={newFiles}
                    learnMoreUrl={learnMoreUrl}
                  />
                ),
              })}
          </>
        ),
        Error: () => null,
        Loading: () => (
          <Spinner text={`Generating ${extensionType} ${route}...`} />
        ),
        NotRequested: () => null,
      }),
    [
      generationTask,
      extensionType,
      route,
      finishedInstallation,
      setFinishedInstallation,
      completeWizard,
      successMessage,
    ]
  );
};
